import { useSafeIntl } from 'bluesquare-components';
import * as yup from 'yup';
import { TEMPORARY } from '../../constants';
import MESSAGES from '../../messages';

yup.addMethod(
    yup.mixed,
    'isNumbersArrayString',
    function isNumbersArrayString(formatMessage) {
        return this.test('isNumbersArrayString', '', (value, context) => {
            const { path, createError } = context;
            let errorMessage;
            if (value) {
                const regexp = /^\d*$/;

                const valuesArray: string[] = Array.isArray(value)
                    ? value.map((v: string | number) => `${v}`.trim())
                    : value
                          .split(',')
                          .map((v: string | number) => `${v}`.trim());
                const hasOtherChar = valuesArray.some(v => !regexp.test(v));
                if (hasOtherChar) {
                    errorMessage = formatMessage(MESSAGES.lotNumberError);
                }
            }

            if (errorMessage) {
                return createError({
                    path,
                    message: errorMessage,
                });
            }
            return true;
        });
    },
);

const hasCampaignOrAlternative = (
    campaign: unknown,
    alternativeCampaign: unknown,
): boolean => {
    const hasCampaign = campaign != null && String(campaign).trim() !== '';
    const hasAlternative =
        alternativeCampaign != null &&
        String(alternativeCampaign).trim() !== '';
    return hasCampaign || hasAlternative;
};

export const useFormAValidation = () => {
    const { formatMessage } = useSafeIntl();
    const requiredMessage = formatMessage(MESSAGES.requiredField);
    return yup.object().shape({
        campaign: yup
            .string()
            .nullable()
            .test(
                'campaign-or-alternative-required',
                requiredMessage,
                function campaignOrAlternative(value) {
                    return hasCampaignOrAlternative(
                        value,
                        this.parent.alternative_campaign,
                    );
                },
            ),
        alternative_campaign: yup
            .string()
            .nullable()
            .test(
                'alternative-campaign-non-empty-string',
                requiredMessage,
                value =>
                    value === undefined ||
                    value === null ||
                    String(value).trim() !== '',
            )
            .test(
                'campaign-or-alternative-required',
                requiredMessage,
                function campaignOrAlternative(value) {
                    return hasCampaignOrAlternative(
                        this.parent.campaign,
                        value,
                    );
                },
            ),
        round: yup
            .number()
            .nullable()
            .when('campaign', {
                is: (campaign: unknown) =>
                    campaign != null && String(campaign).trim() !== '',
                then: schema =>
                    schema
                        .required(requiredMessage)
                        .integer()
                        .typeError(formatMessage(MESSAGES.positiveInteger)),
                otherwise: schema => schema.nullable(),
            }),
        lot_numbers: yup
            .mixed()
            .nullable()
            // .required(formatMessage(MESSAGES.requiredField))
            // TS can't detect the added method
            // @ts-ignore
            .isNumbersArrayString(formatMessage),
        report_date: yup
            .date()
            .required(formatMessage(MESSAGES.requiredField))
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable(),
        form_a_reception_date: yup
            .date()
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable()
            .when('status', {
                is: TEMPORARY,
                then: schema => schema.notRequired(),
                otherwise: schema =>
                    schema.required(formatMessage(MESSAGES.requiredField)),
            }),
        usable_vials_used: yup
            .number()
            .nullable()
            .required(formatMessage(MESSAGES.requiredField))
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        unusable_vials: yup
            .number()
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        doses_per_vial: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        file: yup.mixed().nullable(),
    });
};

export const useDestructionValidation = () => {
    const { formatMessage } = useSafeIntl();
    return yup.object().shape({
        action: yup
            .string()
            .nullable()
            .required(formatMessage(MESSAGES.requiredField)),
        rrt_destruction_report_reception_date: yup
            .date()
            .required()
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable(),
        destruction_report_date: yup
            .date()
            .required(formatMessage(MESSAGES.requiredField))
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable(),
        unusable_vials_destroyed: yup
            .number()
            .nullable()
            .required(formatMessage(MESSAGES.requiredField))
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        doses_per_vial: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        lot_numbers: yup
            .mixed()
            .nullable()
            // TS can't detect the added method
            // @ts-ignore
            .isNumbersArrayString(formatMessage),
        file: yup.mixed().nullable(),
    });
};

export const useIncidentValidation = () => {
    const { formatMessage } = useSafeIntl();
    return yup.object().shape({
        stock_correction: yup
            .string()
            .nullable()
            .required(formatMessage(MESSAGES.requiredField)), // can be made more strict with accepted values from dropdown
        incident_report_received_by_rrt: yup
            .date()
            .required(formatMessage(MESSAGES.requiredField))
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable(),
        date_of_incident_report: yup
            .date()
            .required(formatMessage(MESSAGES.requiredField))
            .typeError(formatMessage(MESSAGES.invalidDate))
            .nullable(),
        movement: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        usable_vials: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        doses_per_vial: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        unusable_vials: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        file: yup.mixed().nullable(),
    });
};

export const useEarmarkValidation = () => {
    const { formatMessage } = useSafeIntl();
    return yup.object().shape({
        earmarked_stock_type: yup
            .string()
            .nullable()
            .required(formatMessage(MESSAGES.requiredField)), // can be made more strict with accepted values from dropdown
        campaign: yup.string().nullable(),
        temporary_campaign_name: yup.string().nullable(),
        round_number: yup.number().integer().positive().nullable(),
        vials_earmarked: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        doses_per_vial: yup
            .number()
            .required(formatMessage(MESSAGES.requiredField))
            .nullable()
            .min(0, formatMessage(MESSAGES.positiveInteger))
            .integer()
            .typeError(formatMessage(MESSAGES.positiveInteger)),
        comment: yup.string().nullable(),
    });
};
